import * as _ from 'lodash-es';
import moment from 'moment';
import React, { Suspense, lazy, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import MetricLabel from '../admin/insights/MetricLabel';
import formatReadTime from '../admin/insights/formatReadTime';
import { METRICS_MAP } from '../admin/insights/metrics';
import RightPanel from '../app/layout/RightPanel';
import Avatar from '../components/Avatar';
import AvatarStack from '../components/AvatarStack';
import ButtonSelect from '../components/ButtonSelect';
import ContextualHelp from '../components/ContextualHelp';
import DateRangePicker from '../components/DateRangePicker';
import Tooltip from '../components/Tooltip';
import { gql } from '../graphql/generated/gql';
import { useGetUser } from '../user/useAllUsers';
import Link from '../utils/router/Link';
import userPath from '../utils/url/userPath';
import PostEditorsChart from './PostEditorsChart';
import * as styles from './PostInsights.module.scss';
import useIsLayoutSize from './utils/useIsLayoutSize';
import { LayoutSize } from './utils/useLayoutSize';

const NewPostInsightsMetrics = lazy(
  () =>
    import(
      /* webpackChunkName: "postMetrics" */
      './NewPostInsightsMetrics'
    ),
);

const PostInsightsMetrics = lazy(
  () =>
    import(
      /* webpackChunkName: "postMetrics" */
      './PostInsightsMetrics'
    ),
);

const NEW_POST_INSIGHTS_QUERY = gql(/** @complexity 11 */ `
  query NewPostInsightsMetrics($id: ID!, $startHour: Datetime!, $endHour: Datetime!, $latestOnly: Boolean!) {
    postInsights(postId: $id) {
      metrics(startHour: $startHour, endHour: $endHour) {
        ...NewPostInsightsMetrics_postInsightsMetric
      }
      reads {
        readTime
        user {
          ...useGetUser_user
        }
      }
      edits(latestOnly: $latestOnly) {
       ...PostEditorsChart_postInsightsEdit
      }
    }
  }
`);

const POST_INSIGHTS_QUERY = gql(/** @complexity 3 */ `
  query postInsights($id: ID!) {
    post(id: $id) {
      id
      insertedAt
      viewers {
        id
        ...AvatarStack_users
      }
    }
  }
`);

type DateRange = {
  startHour: moment.Moment;
  endHour: moment.Moment;
};

interface SectionHeaderProps {
  title: string;
  tooltip?: React.ComponentProps<typeof ContextualHelp>['content'];
  right?: React.ReactNode;
}

const SectionHeader = ({ title, right, tooltip }: SectionHeaderProps) => (
  <div className={styles.sectionHeader}>
    <div className={styles.sectionHeaderTitle}>
      <span>{title}</span>

      {!!tooltip && <ContextualHelp content={tooltip} />}
    </div>
    {!!right && <div>{right}</div>}
  </div>
);

type GetSetType<T> = T extends Set<infer U> ? U : never;
type EnabledMetricsSetType = GetSetType<
  React.ComponentProps<typeof NewPostInsightsMetrics>['enabledMetrics']
>;

interface MetricsSectionProps {
  onDatesChange: React.ComponentProps<typeof DateRangePicker>['onDatesChange'];
  metrics: React.ComponentProps<typeof NewPostInsightsMetrics>['metrics'];
  loading: boolean;
  dateRange: DateRange;
  allTimeStartDate: moment.Moment;
}

const MetricsSection = ({
  onDatesChange,
  metrics,
  loading,
  dateRange,
  allTimeStartDate,
}: MetricsSectionProps) => {
  const [enabledMetrics, setEnabledMetrics] = useState(
    () => new Set<EnabledMetricsSetType>(['views', 'reads']),
  );

  const toggleMetric = (metric: EnabledMetricsSetType) => {
    setEnabledMetrics((state) => {
      const newSet = new Set(state);
      if (state.has(metric)) {
        newSet.delete(metric);
      } else {
        newSet.add(metric);
      }
      return newSet;
    });
  };

  const isMobile = useIsLayoutSize(LayoutSize.mobile);
  const ranges = useMemo((): React.ComponentProps<
    typeof DateRangePicker
  >['ranges'] => {
    return [
      {
        options: [
          { label: '7 days', value: 7 },
          { label: '14 days', value: 14 },
          { label: '30 days', value: 30 },
          { label: '90 days', value: 90 },
        ],
      },
      {
        options: [
          {
            label: 'Last week',
            value: {
              startAt: moment().subtract(1, 'week').startOf('week').toDate(),
              endAt: moment().subtract(1, 'week').endOf('week').toDate(),
            },
          },
          {
            label: 'Last month',
            value: {
              startAt: moment().subtract(1, 'month').startOf('month').toDate(),
              endAt: moment().subtract(1, 'month').endOf('month').toDate(),
            },
          },
          {
            label: 'Last quarter',
            value: {
              startAt: moment()
                .subtract(1, 'quarter')
                .startOf('quarter')
                .toDate(),
              endAt: moment().subtract(1, 'quarter').endOf('quarter').toDate(),
            },
          },
          {
            label: 'Last year',
            value: {
              startAt: moment().subtract(1, 'year').startOf('year').toDate(),
              endAt: moment().subtract(1, 'year').endOf('year').toDate(),
            },
          },
        ],
      },
      isMobile
        ? {
            options: [
              {
                label: 'All time',
                value: {
                  startAt: allTimeStartDate.toDate(),
                  endAt: new Date(),
                },
              },
            ],
          }
        : {
            options: [{ label: 'Custom', value: 'custom' }],
          },
    ];
  }, [isMobile, allTimeStartDate]);

  const totalViews = _.sumBy(metrics, (item) => item.numViews ?? 0);
  const totalReads = _.sumBy(metrics, (item) => item.readTime ?? 0);

  return (
    <div className={styles.section}>
      <SectionHeader
        title="metrics"
        right={
          <DateRangePicker
            initialRange={INITIAL_RANGE}
            onDatesChange={onDatesChange}
            ranges={ranges}
            variant="button"
            mode="daily"
            maxHeight={410}
          />
        }
      />
      <div>
        <div className={styles.metricLabels}>
          <div>
            <MetricLabel
              color={METRICS_MAP.views.color}
              label={METRICS_MAP.views.label}
              enabled={enabledMetrics.has('views')}
              value={totalViews}
              startAt={dateRange.startHour.toDate()}
              endAt={dateRange.endHour.toDate()}
              onClick={() => toggleMetric('views')}
              size="small"
              tooltip={{
                paragraph:
                  "Total number of times this post's contents were loaded and shown. Multiple views by the same user are counted each time",
                button: {
                  newTab: true,
                  // TODO: Add correct link once we have it
                  to: 'https://help.slab.com',
                  text: 'Learn more',
                },
              }}
            />
          </div>
          <div>
            <MetricLabel
              color={METRICS_MAP.reads.color}
              label={METRICS_MAP.reads.label}
              enabled={enabledMetrics.has('reads')}
              value={formatReadTime(totalReads)}
              startAt={dateRange.startHour.toDate()}
              endAt={dateRange.endHour.toDate()}
              onClick={() => toggleMetric('reads')}
              size="small"
              tooltip={{
                paragraph:
                  "Total amount of time users spend reading this post's contents, including in preview hovercards, integrations, and email.",
                button: {
                  newTab: true,
                  // TODO: Add correct link once we have it
                  to: 'https://help.slab.com',
                  text: 'Learn more',
                },
              }}
            />
          </div>
        </div>
        <div className={styles.sectionChart}>
          <Suspense fallback={null}>
            <NewPostInsightsMetrics
              metrics={metrics}
              loading={loading}
              startHour={dateRange.startHour}
              enabledMetrics={enabledMetrics}
            />
          </Suspense>
        </div>
      </div>
    </div>
  );
};

const INITIAL_RANGE = 7;

interface PostInsightsProps {
  id: string;
  showNewPostInsights?: boolean;
}

const PostInsights = ({ id, showNewPostInsights }: PostInsightsProps) => {
  const [isShowingLatestOnlyEdits, setIsShowingLatestOnlyEdits] =
    useState(true);

  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    startHour: moment().add(1, 'h').startOf('h').subtract(INITIAL_RANGE, 'd'),
    endHour: moment().add(1, 'h').startOf('h'),
  }));
  const getUser = useGetUser();
  const { data } = useQuery(POST_INSIGHTS_QUERY, { variables: { id } });

  const newPostInsightsResult = useQuery(NEW_POST_INSIGHTS_QUERY, {
    variables: {
      id,
      startHour: dateRange.startHour.toISOString(),
      endHour: dateRange.endHour.toISOString(),
      latestOnly: isShowingLatestOnlyEdits,
    },
  });

  const metrics = newPostInsightsResult.loading
    ? newPostInsightsResult.previousData?.postInsights?.metrics
    : newPostInsightsResult.data?.postInsights?.metrics;

  const reads = _.compact(
    newPostInsightsResult.loading
      ? newPostInsightsResult.previousData?.postInsights?.reads
      : newPostInsightsResult.data?.postInsights?.reads,
  );
  const readsByUsers = _.keyBy(reads, (r) => r.user?.id ?? '');
  const readers = _.uniqBy(
    _.compact(reads.map((r) => (r.user ? getUser(r.user) : null))),
    'id',
  );

  const edits = newPostInsightsResult.loading
    ? newPostInsightsResult.previousData?.postInsights?.edits
    : newPostInsightsResult.data?.postInsights?.edits;

  return (
    <RightPanel.Inner header={<RightPanel.Header title="Post insights" />}>
      {data ? (
        <div className={styles.container}>
          <div style={{ display: showNewPostInsights ? 'block' : 'none' }}>
            <MetricsSection
              onDatesChange={({ startAt, endAt }, value) => {
                if (value === 'custom') {
                  setDateRange({
                    startHour: moment(startAt).startOf('d'),
                    endHour: moment(endAt).endOf('d'),
                  });
                } else {
                  setDateRange({
                    startHour: moment(startAt).startOf('h'),
                    endHour: moment(endAt).startOf('h'),
                  });
                }
              }}
              metrics={metrics ?? []}
              loading={metrics == null && newPostInsightsResult.loading}
              dateRange={dateRange}
              allTimeStartDate={moment(data.post.insertedAt)}
            />
            <div className={styles.section}>
              <SectionHeader
                title="editors"
                tooltip={{
                  paragraph:
                    'All users who added, removed, or formatted text, sorted by the total number of these edits. Contiguous edits are only counted once.',
                  button: {
                    newTab: true,
                    // TODO: Add correct link once we have it
                    to: 'https://help.slab.com',
                    text: 'Learn more',
                  },
                }}
                right={
                  <ButtonSelect
                    isSearchable={false}
                    isClearable={false}
                    options={[
                      { label: 'Latest version', value: 'latest' },
                      { label: 'All versions', value: 'all' },
                    ]}
                    value={isShowingLatestOnlyEdits ? 'latest' : 'all'}
                    onChange={(value) => {
                      setIsShowingLatestOnlyEdits(value === 'latest');
                    }}
                  />
                }
              />
              <PostEditorsChart edits={edits ?? null} />
            </div>
            <div className={styles.section}>
              <SectionHeader
                title="readers"
                tooltip={{
                  paragraph:
                    "All users who spend at least 30 seconds reading this post's contents. Inactivity is automatically detected and excluded.",
                  button: {
                    newTab: true,
                    // TODO: Add correct link once we have it
                    to: 'https://help.slab.com',
                    text: 'Learn more',
                  },
                }}
              />
              <AvatarStack items={readers}>
                {(user, options) => {
                  const readTime = readsByUsers[user.id]?.readTime;
                  return (
                    <Tooltip
                      content={
                        <Link to={userPath(user)} newTab>
                          <div>{user.name}</div>
                          {readTime != null && (
                            <Tooltip.Muted>
                              {formatReadTime(readTime)}
                            </Tooltip.Muted>
                          )}
                        </Link>
                      }
                      asChild
                      trigger="clickOrHover"
                    >
                      <Avatar user={user} size={options.size} />
                    </Tooltip>
                  );
                }}
              </AvatarStack>
            </div>
          </div>

          {!showNewPostInsights && (
            <>
              <div>
                <Suspense fallback={null}>
                  <PostInsightsMetrics id={id} />
                </Suspense>
              </div>
              {!!data.post.viewers.length && (
                <div className={styles.section}>
                  <SectionHeader title="readers" />
                  <AvatarStack
                    items={data.post.viewers}
                    limit={5}
                    countOverflowTooltip="View readers"
                    expandable
                  >
                    {(user, options) => (
                      <Link to={userPath(user)} newTab>
                        <Avatar user={user} size={options.size} />
                      </Link>
                    )}
                  </AvatarStack>
                </div>
              )}
            </>
          )}
        </div>
      ) : undefined}
    </RightPanel.Inner>
  );
};

export default PostInsights;
